import React, { useState, useEffect } from 'react';
import { Shield, Crown, Lock, AlertTriangle, Info } from 'lucide-react';
import axios from 'axios';
import Badge from '../../components/ui/Badge';
import Alert from '../../components/ui/Alert';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DataTable from '../../components/tables/DataTable';
import SearchBar from '../../components/common/SearchBar';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import usePermissions from '../../hooks/usePermissions';

const API_URL = 'https://medigo-api.onrender.com/api';

const AdminManagement = () => {
  const {
    canCreateAdmins,
    canDeleteAdmins,
    canToggleAdminStatus,
    canViewAdmins,
    isSuperAdmin,
    isAdmin
  } = usePermissions();

  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [submitting, setSubmitting] = useState(false);

  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'admin'
  });

  useEffect(() => {
    fetchAdmins();
  }, [pagination.page, searchTerm]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('medigo_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm
      });

      const response = await axios.get(
        `${API_URL}/admin/admins?${params}`,
        getAuthHeaders()
      );

      if (response.data.success) {
        const data = Array.isArray(response.data.data) ? response.data.data : [];
        setAdmins(data);
        
        if (response.data.pagination) {
          setPagination(prev => ({ 
            ...prev, 
            total: response.data.pagination.total || data.length 
          }));
        }
      }
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    
    if (!canCreateAdmins) {
      setError('Only Super Admins can create admin accounts');
      return;
    }
    
    setError('');
    setSubmitting(true);
    
    try {
      const adminData = {
        name: newAdmin.name.trim(),
        email: newAdmin.email.trim().toLowerCase(),
        phone: newAdmin.phone.trim(),
        password: newAdmin.password,
        role: newAdmin.role
      };
      
      const response = await axios.post(
        `${API_URL}/admin/admins`,
        adminData,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Admin added successfully');
        setShowAddModal(false);
        setNewAdmin({ name: '', email: '', phone: '', password: '', role: 'admin' });
        fetchAdmins();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add admin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!canDeleteAdmins) {
      setError('Only Super Admins can delete admin accounts');
      setShowDeleteDialog(false);
      return;
    }

    try {
      const response = await axios.delete(
        `${API_URL}/admin/admins/${selectedAdmin._id}`,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Admin deleted successfully');
        setShowDeleteDialog(false);
        setSelectedAdmin(null);
        fetchAdmins();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete admin');
      setShowDeleteDialog(false);
    }
  };

  const toggleAdminStatus = async (adminId, adminRole) => {
    if (!canToggleAdminStatus) {
      setError('Only Super Admins can change admin status');
      return;
    }

    if (adminRole === 'superadmin') {
      setError('Cannot deactivate Super Admin accounts');
      return;
    }

    try {
      const response = await axios.patch(
        `${API_URL}/admin/admins/${adminId}/status`,
        {},
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess(response.data.message || 'Admin status updated');
        fetchAdmins();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleOpenAddModal = () => {
    if (!canCreateAdmins) {
      setError('Only Super Admins can create admin accounts');
      return;
    }
    setShowAddModal(true);
  };

  const columns = [
    {
      header: 'Admin',
      accessor: 'name',
      cell: (row) => {
        const isSuperAdminRow = row.role === 'superadmin';
        
        return (
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isSuperAdminRow ? 'bg-purple-100' : 'bg-red-100'
            }`}>
              <span className={`font-semibold ${
                isSuperAdminRow ? 'text-purple-600' : 'text-red-600'
              }`}>
                {row.name?.charAt(0).toUpperCase() || 'A'}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium text-gray-900">{row.name}</p>
                {isSuperAdminRow && (
                  <Crown className="w-4 h-4 text-purple-600" />
                )}
              </div>
              <p className="text-sm text-gray-600">{row.email}</p>
            </div>
          </div>
        );
      },
    },
    {
      header: 'Phone',
      accessor: 'phone',
      cell: (row) => (
        <span className="text-sm text-gray-600">{row.phone || 'N/A'}</span>
      ),
    },
    {
      header: 'Role',
      accessor: 'role',
      cell: (row) => {
        const isSuperAdminRow = row.role === 'superadmin';
        
        return (
          <Badge variant={isSuperAdminRow ? 'warning' : 'error'}>
            {isSuperAdminRow ? (
              <span className="flex items-center gap-1">
                <Crown className="w-3 h-3" />
                Super Admin
              </span>
            ) : (
              'Admin'
            )}
          </Badge>
        );
      },
    },
    {
      header: 'Status',
      accessor: 'isActive',
      cell: (row) => (
        <Badge variant={row.isActive ? 'success' : 'error'}>
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      header: 'Actions',
      accessor: 'actions',
      cell: (row) => {
        const isSuperAdminRow = row.role === 'superadmin';
        
        return (
          <div className="flex items-center gap-2">
            {canToggleAdminStatus && !isSuperAdminRow && (
              <button
                onClick={() => toggleAdminStatus(row._id, row.role)}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  row.isActive
                    ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {row.isActive ? 'Deactivate' : 'Activate'}
              </button>
            )}
            
            {canDeleteAdmins && !isSuperAdminRow && (
              <button
                onClick={() => {
                  setSelectedAdmin(row);
                  setShowDeleteDialog(true);
                }}
                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
              >
                Delete
              </button>
            )}

            {isSuperAdminRow && (
              <span className="flex items-center gap-1 text-sm text-gray-500">
                <Lock className="w-3 h-3" />
                Protected
              </span>
            )}
          </div>
        );
      },
    },
  ];

  if (!canViewAdmins) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-8 h-8 text-red-600" />
            Admin Management
            {isSuperAdmin && <Crown className="w-6 h-6 text-purple-600" />}
          </h1>
          <p className="text-gray-600 mt-1">Manage administrator accounts and permissions</p>
        </div>
        {canCreateAdmins && (
          <button
            onClick={handleOpenAddModal}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <Shield className="w-4 h-4" />
            Add Admin
          </button>
        )}
      </div>

      {isSuperAdmin ? (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-center gap-3">
          <Crown className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <p className="text-purple-900 font-medium">Super Admin Privileges Active</p>
            <p className="text-purple-700 text-sm">
              You can create, delete, and manage all administrator accounts
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
          <div>
            <p className="text-blue-900 font-medium">Admin View Access</p>
            <p className="text-blue-700 text-sm">
              You can view administrators but cannot create or delete accounts
            </p>
          </div>
        </div>
      )}

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="bg-white rounded-lg shadow p-6">
        <SearchBar
          placeholder="Search admins by name or email..."
          onSearch={setSearchTerm}
          value={searchTerm}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Admins</p>
              <p className="text-2xl font-bold text-gray-900">
                {pagination.total || admins.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-green-600">
                {admins.filter(a => a.isActive).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Super Admins</p>
              <p className="text-2xl font-bold text-purple-600">
                {admins.filter(a => a.role === 'superadmin').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Crown className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <DataTable
          columns={columns}
          data={admins}
          loading={loading}
          pagination={pagination}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        />
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Shield className="w-6 h-6 text-red-600" />
                Add New Admin
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewAdmin({ name: '', email: '', phone: '', password: '', role: 'admin' });
                  setError('');
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAddAdmin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="admin@medigo.co.ke"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={newAdmin.phone}
                  onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="+254700000000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Minimum 6 characters"
                />
              </div>
              
              <div className="flex items-center p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <input
                  type="checkbox"
                  id="superAdmin"
                  checked={newAdmin.role === 'superadmin'}
                  onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.checked ? 'superadmin' : 'admin' })}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label htmlFor="superAdmin" className="ml-2 block text-sm">
                  <span className="flex items-center gap-1 text-purple-900 font-medium">
                    <Crown className="w-4 h-4" />
                    Super Admin (Full system access)
                  </span>
                </label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Adding...' : 'Add Admin'}
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => {
                    setShowAddModal(false);
                    setNewAdmin({ name: '', email: '', phone: '', password: '', role: 'admin' });
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteDialog && (
        <ConfirmDialog
          title="Delete Admin"
          message={`Are you sure you want to delete ${selectedAdmin?.name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDeleteAdmin}
          onCancel={() => {
            setShowDeleteDialog(false);
            setSelectedAdmin(null);
          }}
          type="danger"
        />
      )}
    </div>
  );
};

export default AdminManagement;